import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ConfirmDialogComponentDialog, DialogData } from '../../confirm-dialog/confirm-dialog.component';
import { InformationDialogComponent } from '../../information-dialog/information-dialog/information-dialog.component';

/**
 * Opens the app's confirm and information dialogs from anywhere, including the places that have no host
 * component to hang a template off: the unsaved-changes guard and the token interceptor. ModalComponent
 * cannot serve those, because it opens itself from a template in ngAfterViewInit.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {

  // These open on top of the form modals, which are NgbModals of their own, so they need to outrank them
  // rather than rely on being later in the DOM. The Material overlay container was pinned above
  // everything for the same reason.
  private static readonly options = {
    windowClass: "app-dialog",
    backdropClass: "app-dialog-backdrop"
  };

  constructor(private readonly modalService: NgbModal) { }

  /**
   * Reports which action the user took. Walking away - backdrop, Escape - resolves to null rather than
   * false, so a caller can still tell that apart from an outright No.
   */
  confirm(data: DialogData): Observable<boolean | null> {
    const modal = this.modalService.open(ConfirmDialogComponentDialog, DialogService.options);
    modal.componentInstance.data = data;

    return this.resultOf(modal.result);
  }

  inform(message: string): Observable<boolean | null> {
    const modal = this.modalService.open(InformationDialogComponent, DialogService.options);
    modal.componentInstance.message = message;

    return this.resultOf(modal.result);
  }

  // NgbModalRef.result rejects on dismiss where MatDialogRef.afterClosed() simply emitted nothing, so the
  // rejection is mapped back to a null emission to keep the existing call sites' `result == null` checks.
  private resultOf(result: Promise<boolean>): Observable<boolean | null> {
    return from(result).pipe(catchError(() => of(null)));
  }
}
